import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import CosmicTyper, { type CosmicTyperResult } from './CosmicTyper.jsx';

/**
 * CosmicTyperOverlay — full-viewport launcher for the Cosmic Typing game.
 *
 * Mounts a fixed-position black backdrop above every other layer (z-[80] —
 * the BookDetailModal sheet sits at z-50, and we explicitly want to cover
 * it so the game truly fills the screen). A small "Back" pill in the
 * corner closes the overlay and returns the user to whatever was below
 * (e.g. the still-open book modal).
 *
 * Side effects:
 *   - Locks <body> scroll while open so background scrolling doesn't peek
 *     through the gap between the game's spawn area and the viewport edge
 *     on short windows.
 *   - Listens for the Escape key to close. Radix Dialog already handles
 *     Escape for the modal underneath, but this overlay isn't a Dialog,
 *     so we wire our own.
 */
export interface CosmicTyperOverlayProps {
  open: boolean;
  /** Words pulled from the active book's description. Optional — falls back
   *  to the grade's built-in pool inside the component. */
  words?: string[];
  grade?: 3 | 4 | 5;
  onClose: () => void;
  /**
   * Fired when a run ends. The caller decides what to do with the result
   * (typically: POST it to the leaderboard endpoint along with the
   * surrounding book context). The game keeps running its own end-screen
   * UI — this callback is fire-and-forget.
   */
  onGameOver?: (result: CosmicTyperResult) => void;
}

export function CosmicTyperOverlay({
  open,
  words,
  grade = 4,
  onClose,
  onGameOver,
}: CosmicTyperOverlayProps) {
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black" role="dialog" aria-label="Cosmic Typing game">
      <button
        type="button"
        onClick={onClose}
        className="absolute left-4 top-4 z-[81] inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[12px] font-semibold text-or-slate-800 shadow-or-card backdrop-blur transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
        aria-label="Exit Cosmic Typing"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back
      </button>
      <div className="h-full w-full">
        <CosmicTyper words={words} grade={grade} onGameOver={onGameOver} />
      </div>
    </div>
  );
}
